import { useState, useEffect } from "react";
import api from "../api";
import { useTheme } from "../components/Theme";

const ORANGE = "#E8520A";

const STAGE_LABELS = {
  prospeccao: "Prospecção", contato: "Contato", reuniao: "Reunião",
  proposta: "Proposta", fechado: "Fechado", perdido: "Perdido"
};

function fmt(v) { return `R$${Number(v || 0).toLocaleString("pt-BR")}`; }

function MedalIcon({ pos }) {
  if (pos === 1) return <span style={{ fontSize: 22 }}>🥇</span>;
  if (pos === 2) return <span style={{ fontSize: 22 }}>🥈</span>;
  if (pos === 3) return <span style={{ fontSize: 22 }}>🥉</span>;
  return <span style={{ fontSize: 16, color: "#555", fontWeight: 700 }}>#{pos}</span>;
}

function ProgressBar({ value, max, color, height = 8 }) {
  const { theme } = useTheme();
  const pct = max ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ background: theme.bgInput, borderRadius: 4, height, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, background: color || ORANGE, height: "100%", borderRadius: 4, transition: "width .5s ease" }} />
    </div>
  );
}

export default function PainelVendas({ currentUser }) {
  const { theme } = useTheme();
  const [leads,    setLeads]    = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [periodo,  setPeriodo]  = useState("mes"); // mes | semana | total

  useEffect(() => {
    Promise.all([
      api.get("/leads?limit=1000"),
      api.get("/users"),
    ]).then(([leadsRes, usersRes]) => {
      setLeads(leadsRes.data.leads);
      setUsuarios(usersRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Filtrar por período
  const filtrarPeriodo = (leads) => {
    const agora = new Date();
    return leads.filter(l => {
      const d = new Date(l.createdAt);
      if (periodo === "semana") { const diff = (agora - d) / (1000*60*60*24); return diff <= 7; }
      if (periodo === "mes")    { return d.getMonth() === agora.getMonth() && d.getFullYear() === agora.getFullYear(); }
      return true;
    });
  };

  const leadsFiltrados = filtrarPeriodo(leads);

  // Métricas por vendedor
  const metricasPorVendedor = usuarios.map(u => {
    const meus = leadsFiltrados.filter(l => l.nomeResponsavel === u.nome);
    const fechados = meus.filter(l => l.stage === "fechado");
    const perdidos = meus.filter(l => l.stage === "perdido");
    const emAndamento = meus.filter(l => !["fechado","perdido"].includes(l.stage));
    const mrr = fechados.reduce((a, l) => a + Number(l.valor || 0), 0);
    const pipeline = emAndamento.reduce((a, l) => a + Number(l.valor || 0), 0);
    const taxaConv = meus.length ? Math.round((fechados.length / meus.length) * 100) : 0;
    return { usuario: u, total: meus.length, fechados: fechados.length, perdidos: perdidos.length, emAndamento: emAndamento.length, mrr, pipeline, taxaConv };
  }).filter(m => m.total > 0 || currentUser?.role === "admin")
    .sort((a, b) => b.mrr - a.mrr || b.fechados - a.fechados);

  // Métricas gerais
  const totalLeads    = leadsFiltrados.length;
  const totalFechados = leadsFiltrados.filter(l => l.stage === "fechado").length;
  const totalMRR      = leadsFiltrados.filter(l => l.stage === "fechado").reduce((a, l) => a + Number(l.valor || 0), 0);
  const totalPipeline = leadsFiltrados.filter(l => !["fechado","perdido"].includes(l.stage)).reduce((a, l) => a + Number(l.valor || 0), 0);
  const taxaGeral     = totalLeads ? Math.round((totalFechados / totalLeads) * 100) : 0;
  const maxMRR        = Math.max(...metricasPorVendedor.map(m => m.mrr), 1);
  const maxFechados   = Math.max(...metricasPorVendedor.map(m => m.fechados), 1);

  // Leads por origem
  const porOrigem = leadsFiltrados.reduce((acc, l) => {
    acc[l.origem] = (acc[l.origem] || 0) + 1; return acc;
  }, {});
  const origensOrdenadas = Object.entries(porOrigem).sort((a, b) => b[1] - a[1]);
  const maxOrigem = Math.max(...Object.values(porOrigem), 1);

  const PERIODO_LABELS = { semana: "Esta semana", mes: "Este mês", total: "Todo o período" };

  if (loading) return <div style={{ color: theme.textMuted, padding: 40, textAlign: "center", fontFamily: "'Barlow Condensed',sans-serif", fontSize: 18, letterSpacing: 2 }}>CARREGANDO...</div>;

  return (
    <div style={{ padding: 24, fontFamily: "'Barlow',sans-serif", background: theme.bg, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: 2, color: theme.text }}>PAINEL DE VENDAS</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: theme.textMuted }}>{PERIODO_LABELS[periodo]}</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {Object.entries(PERIODO_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setPeriodo(key)} style={{
              padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: `1px solid ${periodo === key ? ORANGE + "66" : theme.border}`,
              background: periodo === key ? ORANGE + "22" : theme.bgCard,
              color: periodo === key ? ORANGE : theme.textMuted
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Métricas gerais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          ["Total de Leads", totalLeads, theme.text, "leads no período"],
          ["Clientes Fechados", totalFechados, "#10b981", `taxa ${taxaGeral}%`],
          ["MRR Gerado", fmt(totalMRR), ORANGE, "receita mensal"],
          ["Pipeline", fmt(totalPipeline), "#6366f1", "em negociação"],
        ].map(([label, val, color, sub]) => (
          <div key={label} style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 11, color: theme.textMuted, letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: "'Barlow Condensed',sans-serif" }}>{val}</div>
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 3 }}>{sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, alignItems: "start" }}>
        {/* Ranking do time */}
        <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${theme.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textSub, letterSpacing: ".04em", textTransform: "uppercase" }}>🏆 Ranking do Time</h3>
            <span style={{ fontSize: 12, color: theme.textMuted }}>{metricasPorVendedor.length} vendedor{metricasPorVendedor.length !== 1 ? "es" : ""}</span>
          </div>

          {metricasPorVendedor.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: theme.textMuted, fontSize: 13 }}>
              Nenhum lead cadastrado no período. Comece adicionando leads no Kanban!
            </div>
          ) : (
            metricasPorVendedor.map((m, idx) => (
              <div key={m.usuario._id} style={{
                padding: "16px 20px", borderBottom: `1px solid ${theme.border}`,
                background: idx === 0 ? ORANGE + "08" : "transparent"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                  <MedalIcon pos={idx + 1} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 700, color: theme.text, fontSize: 15 }}>{m.usuario.nome}</span>
                        {m.usuario.role === "admin" && <span style={{ fontSize: 10, color: ORANGE, marginLeft: 6 }}>⚡ Admin</span>}
                        {m.usuario._id === currentUser?._id && <span style={{ fontSize: 10, color: theme.textMuted, marginLeft: 6 }}>• você</span>}
                      </div>
                      <span style={{ fontWeight: 800, color: "#10b981", fontSize: 16, fontFamily: "'Barlow Condensed',sans-serif" }}>{fmt(m.mrr)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                      {m.total} leads · {m.fechados} fechados · {m.taxaConv}% conversão
                    </div>
                  </div>
                </div>

                {/* Barra MRR */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>
                    <span>MRR gerado</span><span style={{ color: "#10b981" }}>{fmt(m.mrr)}</span>
                  </div>
                  <ProgressBar value={m.mrr} max={maxMRR} color="#10b981" height={6} />
                </div>

                {/* Barra Pipeline */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>
                    <span>Pipeline</span><span style={{ color: "#6366f1" }}>{fmt(m.pipeline)}</span>
                  </div>
                  <ProgressBar value={m.pipeline} max={Math.max(...metricasPorVendedor.map(x => x.pipeline), 1)} color="#6366f1" height={6} />
                </div>

                {/* Stats */}
                <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                  {[
                    [m.fechados, "Fechados", "#10b981"],
                    [m.emAndamento, "Em andamento", "#f59e0b"],
                    [m.perdidos, "Perdidos", "#ef4444"],
                    [`${m.taxaConv}%`, "Conversão", "#6366f1"],
                  ].map(([val, label, color]) => (
                    <div key={label} style={{ background: theme.bgInput, borderRadius: 8, padding: "6px 12px", textAlign: "center", flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color, fontFamily: "'Barlow Condensed',sans-serif" }}>{val}</div>
                      <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 1 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Coluna lateral */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Meta do mês */}
          <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: theme.textSub, letterSpacing: ".04em", textTransform: "uppercase" }}>🎯 Meta — 500 Clientes</h3>
            <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
              <span>Clientes fechados</span>
              <span style={{ color: "#10b981", fontWeight: 700 }}>{leads.filter(l => l.stage === "fechado").length} / 500</span>
            </div>
            <ProgressBar value={leads.filter(l => l.stage === "fechado").length} max={500} color={ORANGE} height={12} />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6, textAlign: "center" }}>
              {Math.round((leads.filter(l => l.stage === "fechado").length / 500) * 100)}% da meta anual
            </div>
          </div>

          {/* Top origens */}
          <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: theme.textSub, letterSpacing: ".04em", textTransform: "uppercase" }}>📊 Origens dos Leads</h3>
            {origensOrdenadas.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: 13 }}>Nenhum dado no período.</p>
            ) : origensOrdenadas.map(([origem, count]) => (
              <div key={origem} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: theme.textSub }}>{origem}</span>
                  <span style={{ color: ORANGE, fontWeight: 700 }}>{count}</span>
                </div>
                <ProgressBar value={count} max={maxOrigem} color={ORANGE} height={6} />
              </div>
            ))}
          </div>

          {/* Distribuição por estágio */}
          <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: theme.textSub, letterSpacing: ".04em", textTransform: "uppercase" }}>📋 Por Estágio</h3>
            {Object.entries(
              leadsFiltrados.reduce((acc, l) => { acc[l.stage] = (acc[l.stage] || 0) + 1; return acc; }, {})
            ).sort((a,b) => b[1]-a[1]).map(([stage, count]) => {
              const COLORS = { prospeccao:"#6366f1", contato:"#f59e0b", reuniao:"#3b82f6", proposta:"#8b5cf6", fechado:"#10b981", perdido:"#ef4444" };
              return (
                <div key={stage} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${theme.border}`, fontSize: 13 }}>
                  <span style={{ color: theme.textSub }}>{STAGE_LABELS[stage]}</span>
                  <span style={{ color: COLORS[stage], fontWeight: 700 }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
